import { PropsWithChildren, useEffect, useState } from "react";
import { Button } from '../../components/BaseComponents'
import useApi from "../../hooks/useApi";
import { getCSRFToken } from "../../lib/utils";

type BoltOnProps = PropsWithChildren<{
    label: string;
    featureKey: string;
    companyId: string;
}>

function BoltOn({label, companyId, featureKey, children}: BoltOnProps) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>()
    const [isAdmin, setIsAdmin] = useState(false);
    const api = useApi();

    useEffect(() => {
        setLoading(true);
        let cancelled = false;
        const run = async () => {
            const result = await api.get(`admin/company/${companyId}/boltOns/${featureKey}`)
            if(cancelled) {
                return;
            }
            setData(result.boltOn);
            setIsAdmin(result.isAdmin);
            setLoading(false);
        }
        run();

        return () => {
            cancelled = true;
        }
    }, [featureKey])

    const onSubmit = async (enabled: boolean) => {
        setLoading(true);
        const result = await api.put(`admin/company/${companyId}/boltOns/${featureKey}`, {
            boltOn: { enabled },
            _csrf: await getCSRFToken()
        })
        setData(result.boltOn);
        setLoading(false);
    }

    return (
        <div className="p-2 rounded text-white bg-charcoal-400">
            <div className="flex">
                <h4 className="w-40 text-white">{ label }</h4>
                { isAdmin ? (
                    <div>Organisation is Admin</div>
                ) : (
                    <BoltOnControl
                        isActive={data?.enabled}
                        onSubmit={onSubmit}
                        loading={loading}
                    />
                )}
            </div>
            <div className="mt-2">
                { children }
            </div>
        </div>
    );
}

type BoltOnControlProps = {
    isActive: boolean;
    onSubmit: (enabled: boolean) => void,
    loading?: boolean;
}

function BoltOnControl({isActive, onSubmit, loading}: BoltOnControlProps) {
    if((isActive)) {
        return (
            <div className="flex space-x-2">
                <Button type="Primary" size="sm" text="Enabled" disabled />
                <Button type="Outlined" size="sm" text="Disable" onClick={() => onSubmit(false)} />
            </div>
        );
    }

    return (
        <div className="flex space-x-2">
            <Button type="Outlined" size="sm" text="Enable" onClick={() => onSubmit(true)} />
            <Button type="Primary" size="sm" text="Disabled" disabled additionalClass="!bg-error-500 !border-error-500 !text-error-200" />
        </div>
    );
}

type AdminCompanyBoltOnsProps = {
    companyId: string;
}

// Keys must match FEATURE_BOLT_ON_KEYS in backend/app/constants/featureBoltOns.ts.
export const BOLT_ONS = [
    { featureKey: 'portfolios', label: 'Portfolios', description: 'Curate portfolios.' },
    { featureKey: 'client_accounting', label: 'Client Accounting', description: 'Manage carbon accounting data for clients companies.' },
    { featureKey: 'projects', label: 'Projects', description: 'Group emissions for projects.' },
    { featureKey: 'company_groups', label: 'Company Groups', description: 'Not yet wired up: switching this on or off has no effect on the organisation for now.' },
    { featureKey: 'screen_scrape', label: 'Screen-scraping', description: 'Can select & copy data from reports.' },
]

function AdminCompanyBoltOns({companyId}: AdminCompanyBoltOnsProps) {

    return (
        <div>
            <h3 className="text-white mb-3 mt-8">Bolt Ons</h3>
            <div className="rounded bg-charcoal-600 p-5 space-y-5">
                {BOLT_ONS.map(({featureKey, label, description}) => (
                    <BoltOn key={featureKey} companyId={companyId} label={label} featureKey={featureKey}>
                        <p>{description}</p>
                    </BoltOn>
                ))}
            </div>
        </div>
    )
}

export default AdminCompanyBoltOns;
